/*
 * Rennsieger — Notify Me (bell) capture.
 *
 * Opens the modal from any [data-rsg-notify] trigger, collects email + vehicle
 * + optional phone, writes the profile to Klaviyo, then plays the success
 * sequence from rsg-notify-me-code-spec.md §5.
 *
 * ---------------------------------------------------------------------------
 * WHY TWO KLAVIYO CALLS, AND WHY IN THIS ORDER
 * ---------------------------------------------------------------------------
 * rsg-klaviyo-backend-architecture.md §7 describes ONE call: "POST a profile
 * subscription to list T8NUf3 with the properties". That call no longer exists.
 * Klaviyo removed custom properties from the subscribe endpoint:
 *
 *   "You can no longer add custom properties to a profile using this endpoint
 *    ... You cannot subscribe a profile and edit custom properties with one
 *    API request."
 *   — developers.klaviyo.com/en/reference/create_client_subscription
 *
 * So it is:
 *   1. POST /client/profiles/       -> the vehicle_* and interest_* properties
 *   2. POST /client/subscriptions/  -> list membership + email/SMS consent
 *
 * Klaviyo's own docs suggest subscribing FIRST and updating properties after.
 * We deliberately do the opposite. The E1 confirmation flow triggers on
 * "Added to list T8NUf3" and its subject line renders
 * {{ vehicle_chassis }} {{ interest_label }}. Subscribe first and the flow can
 * fire before the properties land, sending "You're on the list for your ."
 * Properties first closes that race. Do not reorder these.
 *
 * The trade-off: if call 1 succeeds and call 2 fails we have an orphan profile
 * with no list membership. That is recoverable in Klaviyo. The reverse — a
 * subscriber with no vehicle data — silently breaks every segment in §3, so it
 * is the worse failure and we design against it.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var KLAVIYO_HOST = 'https://a.klaviyo.com';
  // Pinned deliberately. Klaviyo versions by date and an unpinned request
  // silently follows whatever they ship next; a payload that works today
  // should keep working until someone chooses to move it.
  var KLAVIYO_REVISION = '2026-07-15';

  var modal, scrim, form, success, circle, check, errorEl, contextEl;
  var lastTrigger = null;
  var ctx = {};
  var running = false;
  var submitting = false;

  /* ------------------------------------------------------------------ *
   * Vehicle normalisation
   *
   * The YMM fields are free text (Sully's call — dropdowns cost completion).
   * That means "bmw", "BMW ", "Bimmer" all arrive as distinct values and the
   * make baskets in backend-architecture §3 would silently miss people. These
   * two helpers rescue what is safely rescuable, invisibly, at submit time.
   * ------------------------------------------------------------------ */

  // Canonical makes per backend-architecture §1. Anything not recognised is
  // title-cased and passed through rather than dropped — an unknown make is
  // still a real signal about what to stock.
  var MAKE_ALIASES = {
    bmw: 'BMW', bimmer: 'BMW', beemer: 'BMW',
    mercedes: 'Mercedes-Benz', 'mercedes-benz': 'Mercedes-Benz', 'mercedes benz': 'Mercedes-Benz',
    merc: 'Mercedes-Benz', benz: 'Mercedes-Benz', mb: 'Mercedes-Benz', amg: 'Mercedes-Benz',
    audi: 'Audi',
    porsche: 'Porsche', porshe: 'Porsche', porche: 'Porsche',
    dodge: 'Dodge',
    ford: 'Ford',
    vw: 'Volkswagen', volkswagen: 'Volkswagen'
  };

  function normaliseMake(raw) {
    var v = (raw || '').trim().replace(/\s+/g, ' ');
    if (!v) return '';
    var key = v.toLowerCase();
    if (MAKE_ALIASES[key]) return MAKE_ALIASES[key];
    // Title-case as a fallback so "audi" and "Audi" at least collapse together.
    return v.replace(/\b[a-z]/g, function (c) { return c.toUpperCase(); });
  }

  /*
   * Pull a chassis code out of the model string — but ONLY on a confident
   * match against real chassis families. An empty vehicle_chassis is
   * recoverable later; a wrong one poisons the chassis baskets permanently,
   * so anything ambiguous is left unset on purpose.
   *
   * Note the digit counts. They are what keep trims out: "C63" and "M3" are
   * trim badges, not chassis, and neither matches. "C205" and "G80" do.
   */
  function extractChassis(model, make) {
    var m = (model || '').toUpperCase();
    var patterns = [
      /\b([EFGU]\d{2,3})\b/,        // BMW: E90, F30, G80, U06
      /\b([WCRSXAV]\d{3})\b/,       // Mercedes: W205, C118, R231, X167
      /\b(99[1-7]|98[1-7]|718|982)\b/ // Porsche: 991, 992, 996, 981, 718
    ];
    // S550/S650 is a Mustang chassis, but also a Mercedes S-Class trim badge.
    // Only read it as a chassis when the make already resolved to Ford.
    if (make === 'Ford') patterns.push(/\b(S5[05]0|S650)\b/);

    for (var i = 0; i < patterns.length; i++) {
      var hit = m.match(patterns[i]);
      if (hit) return hit[1];
    }
    return '';
  }

  // E.164 for Klaviyo. Bare 10-digit input is assumed North American, which is
  // right for this store; anything already carrying a + is left alone.
  function normalisePhone(raw) {
    var v = (raw || '').trim();
    if (!v) return '';
    if (v.charAt(0) === '+') return '+' + v.slice(1).replace(/\D/g, '');
    var digits = v.replace(/\D/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
    return digits ? '+' + digits : '';
  }

  function validEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((v || '').trim());
  }

  /* ------------------------------------------------------------------ *
   * Klaviyo
   * ------------------------------------------------------------------ */
  function klaviyoPost(path, body, companyId) {
    return fetch(KLAVIYO_HOST + path + '?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      // Both client endpoints answer 202 Accepted on success.
      if (res.status === 202 || res.ok) return true;
      return res.text().then(function (t) {
        throw new Error('Klaviyo ' + res.status + ': ' + t.slice(0, 300));
      });
    });
  }

  function writeProperties(data, companyId) {
    var props = {
      vehicle_year: data.year,
      vehicle_make: data.make,
      vehicle_model: data.model,
      product_of_interest: ctx.handle,
      interest_label: ctx.label,
      interest_category: ctx.category,
      waitlist_source: 'notify-me:' + (ctx.handle || 'unknown'),
      // The consent CHECKBOX state, kept as a property as well as on the
      // subscription record. See the consent note in submit() for why.
      marketing_consent: !!data.consent
    };
    // Only send keys that actually have a value — writing "" onto a profile
    // property is worse than leaving it unset, because a segment rule of
    // "is set" then matches an empty profile.
    if (data.chassis) props.vehicle_chassis = data.chassis;
    if (ctx.collectionId) props.product_of_interest_id = ctx.collectionId;
    if (ctx.image) props.interest_image_url = ctx.image;
    if (data.smsConsent) props.sms_consent = true;
    if (ctx.source) props.signup_source = ctx.source;

    Object.keys(props).forEach(function (k) {
      if (props[k] === '' || props[k] === undefined || props[k] === null) delete props[k];
    });

    return klaviyoPost('/client/profiles/', {
      data: { type: 'profile', attributes: { email: data.email, properties: props } }
    }, companyId);
  }

  function subscribe(data, companyId, listId) {
    var subs = {};

    /*
     * Email consent, and a judgment call worth flagging.
     *
     * Submitting this form IS a request to be emailed about this one launch,
     * so the profile is subscribed to T8NUf3 regardless of the checkbox. The
     * checkbox extends that to BROADER marketing, and is carried as the
     * `marketing_consent` property so campaigns can filter on it while the
     * launch flow still reaches everyone who asked.
     *
     * CASL: confirm this split with counsel before the first send. Both source
     * docs call for legal review and neither resolves this specific case.
     */
    subs.email = { marketing: { consent: 'SUBSCRIBED' } };

    var attributes = { email: data.email, subscriptions: subs };

    // SMS is only ever subscribed on its own explicit checkbox, never bundled
    // with the email opt-in (deliverable spec §6 — SMS is stricter under CASL).
    if (data.phone && data.smsConsent) {
      attributes.phone_number = data.phone;
      subs.sms = { marketing: { consent: 'SUBSCRIBED' } };
    }

    return klaviyoPost('/client/subscriptions/', {
      data: {
        type: 'subscription',
        attributes: { profile: { data: { type: 'profile', attributes: attributes } } },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    }, companyId);
  }

  /* ------------------------------------------------------------------ *
   * Success sequence — orchestration lifted from the tuned demo
   * ------------------------------------------------------------------ */
  var getMs = function (v) {
    return parseFloat(getComputedStyle(document.documentElement).getPropertyValue(v)) || 0;
  };

  // transitionend filtered to one property, fires once. Without the property
  // filter this chain double-fires on any element with more than one
  // transition, and the sequence skips a step.
  function once(el, evt, prop, cb) {
    var h = function (e) {
      if (e.propertyName !== prop) return;
      el.removeEventListener(evt, h);
      cb();
    };
    el.addEventListener(evt, h);
  }

  function runSuccess() {
    if (running) return;
    running = true;

    form.classList.add('is-hidden');                    // 1 · fade the form out
    setTimeout(function () {
      form.style.display = 'none';
      success.classList.add('is-shown');
      void success.offsetWidth;                         // reflow so the draw plays
      success.classList.add('is-drawing');              // 2 · ring draws CW from north

      once(circle, 'transitionend', 'stroke-dashoffset', function () {
        success.classList.add('is-done', 'is-checking'); // 3 · flip to green + draw tick

        once(check, 'transitionend', 'stroke-dashoffset', function () {
          setTimeout(function () {                      // 4 · hold, then fade away
            scrim.classList.add('is-closing');
            once(scrim, 'transitionend', 'opacity', function () {
              closeModal(true);
              resetForm();
            });
          }, getMs('--rsg-nf-hold'));
        });
      });
    }, getMs('--rsg-nf-formout'));
  }

  function resetForm() {
    running = false;
    submitting = false;
    success.classList.remove('is-shown', 'is-drawing', 'is-done', 'is-checking');
    form.classList.remove('is-hidden');
    form.style.display = '';
    form.reset();
    errorEl.textContent = '';
    collapseSms();
    var btn = form.querySelector('[data-rsg-notify-submit]');
    if (btn) { btn.disabled = false; btn.textContent = 'Notify me at launch'; }
  }

  /* ------------------------------------------------------------------ *
   * Open / close
   * ------------------------------------------------------------------ */
  function focusables() {
    return Array.prototype.slice.call(
      scrim.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter(function (el) { return el.offsetParent !== null; });
  }

  function openModal(trigger) {
    lastTrigger = trigger || null;

    ctx = {
      handle: trigger ? trigger.getAttribute('data-collection') || '' : '',
      collectionId: trigger ? trigger.getAttribute('data-collection-id') || '' : '',
      label: trigger ? trigger.getAttribute('data-interest-label') || '' : '',
      category: trigger ? trigger.getAttribute('data-interest-category') || '' : '',
      image: trigger ? trigger.getAttribute('data-interest-image') || '' : '',
      // WHERE this capture came from — a landing category card, a coming-soon nav
      // item, or an out-of-stock product. Written to Klaviyo so every list add is
      // attributable to its surface.
      source: trigger ? trigger.getAttribute('data-notify-source') || '' : ''
    };

    // "we'll alert you the moment your Bucket Seats land" — the sentence is
    // written to read correctly with or without the substitution.
    if (contextEl) {
      contextEl.textContent = ctx.label ? 'your ' + ctx.label : 'it';
    }

    resetForm();
    scrim.classList.remove('is-closing');
    scrim.classList.add('is-open');
    scrim.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';

    // Focus the email field, not the close button — the first thing we ask for
    // should be the first thing under the cursor.
    var email = scrim.querySelector('[data-rsg-notify-email]');
    if (email) setTimeout(function () { email.focus(); }, 60);
  }

  function closeModal(skipFocus) {
    scrim.classList.remove('is-open', 'is-closing');
    scrim.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (!skipFocus && lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
    }
  }

  function isOpen() { return scrim.classList.contains('is-open'); }

  /* ------------------------------------------------------------------ *
   * SMS progressive reveal
   * ------------------------------------------------------------------ */
  var smsPanel, smsToggle;

  function collapseSms() {
    if (!smsPanel || !smsToggle) return;
    smsPanel.style.maxHeight = null;
    smsToggle.setAttribute('aria-expanded', 'false');
  }

  function initSms() {
    smsToggle = scrim.querySelector('[data-rsg-notify-smstoggle]');
    smsPanel = scrim.querySelector('[data-rsg-notify-sms]');
    if (!smsToggle || !smsPanel) return;

    smsToggle.addEventListener('click', function () {
      var expanded = smsToggle.getAttribute('aria-expanded') === 'true';
      if (expanded) {
        collapseSms();
      } else {
        smsPanel.style.maxHeight = smsPanel.scrollHeight + 'px';
        smsToggle.setAttribute('aria-expanded', 'true');
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Submit
   * ------------------------------------------------------------------ */
  function submit(e) {
    e.preventDefault();
    if (submitting) return;

    var emailEl = scrim.querySelector('[data-rsg-notify-email]');
    var email = (emailEl.value || '').trim();

    errorEl.textContent = '';
    emailEl.classList.remove('is-invalid');

    if (!validEmail(email)) {
      emailEl.classList.add('is-invalid');
      errorEl.textContent = 'Please enter a valid email address.';
      emailEl.focus();
      return;
    }

    var companyId = scrim.getAttribute('data-klaviyo-company');
    var listId = scrim.getAttribute('data-klaviyo-list');

    if (!companyId) {
      // Fails loudly rather than swallowing the submission. A silent no-op here
      // would look like a working form while every capture went nowhere.
      errorEl.textContent = 'Signup is not configured yet. Please try again shortly.';
      console.error('[rsg-notify] Missing Klaviyo public key. Set it in Theme settings > Rennsieger > Klaviyo.');
      return;
    }

    var make = normaliseMake(scrim.querySelector('[data-rsg-notify-make]').value);
    var model = (scrim.querySelector('[data-rsg-notify-model]').value || '').trim();

    var data = {
      email: email,
      year: (scrim.querySelector('[data-rsg-notify-year]').value || '').trim(),
      make: make,
      model: model,
      chassis: extractChassis(model, make),
      phone: normalisePhone(scrim.querySelector('[data-rsg-notify-phone]').value),
      consent: scrim.querySelector('[data-rsg-notify-consent]').checked,
      smsConsent: scrim.querySelector('[data-rsg-notify-smsconsent]').checked
    };

    submitting = true;
    var btn = form.querySelector('[data-rsg-notify-submit]');
    btn.disabled = true;
    btn.textContent = 'Adding you...';

    // Properties before subscription. See the header comment — this ordering
    // is what keeps the confirmation email from rendering empty variables.
    writeProperties(data, companyId)
      .catch(function (err) {
        // Non-fatal: losing the properties is bad, losing the contact is worse,
        // so we log and still attempt the subscribe.
        console.error('[rsg-notify] profile properties failed', err);
      })
      .then(function () {
        return subscribe(data, companyId, listId);
      })
      .then(function () {
        runSuccess();
      })
      .catch(function (err) {
        console.error('[rsg-notify] subscribe failed', err);
        submitting = false;
        btn.disabled = false;
        btn.textContent = 'Notify me at launch';
        errorEl.textContent = 'Something went wrong on our end. Please try again.';
      });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function init() {
    scrim = document.querySelector('[data-rsg-notify-modal]');
    if (!scrim) return;

    // Portal to <body>. The modal ships inside a section wrapper, and those
    // wrappers create their own stacking contexts — the hero lockup paints over
    // an open dialog otherwise, whatever z-index says. Idempotent, so re-running
    // on shopify:section:load is safe. Same fix the drawer uses.
    if (scrim.parentElement !== document.body) document.body.appendChild(scrim);

    if (scrim.dataset.rsgBound === '1') return;
    scrim.dataset.rsgBound = '1';

    modal = scrim.querySelector('.rsg-nf-modal');
    form = scrim.querySelector('[data-rsg-notify-form]');
    success = scrim.querySelector('[data-rsg-notify-success]');
    circle = scrim.querySelector('.rsg-nf-circle');
    check = scrim.querySelector('.rsg-nf-check');
    errorEl = scrim.querySelector('[data-rsg-notify-error]');
    contextEl = scrim.querySelector('[data-rsg-notify-context]');
    if (!form || !success || !circle || !check) return;

    // Measure the tick so the draw ends exactly on its last point rather than
    // near it. The CSS placeholder is only there to keep the initial state hidden.
    document.documentElement.style.setProperty('--rsg-nf-check-len', check.getTotalLength());

    form.addEventListener('submit', submit);
    initSms();

    scrim.querySelectorAll('[data-rsg-notify-close]').forEach(function (b) {
      b.addEventListener('click', function () { closeModal(); });
    });

    scrim.addEventListener('click', function (e) {
      if (e.target === scrim && !running) closeModal();
    });

    document.addEventListener('keydown', function (e) {
      if (!isOpen()) return;

      // Esc is ignored mid-animation: the sequence closes the modal itself, and
      // letting Esc race it leaves the form hidden behind a dismissed scrim.
      if (e.key === 'Escape' && !running) {
        e.preventDefault();
        closeModal();
        return;
      }

      if (e.key === 'Tab') {
        var items = focusables();
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    });
  }

  function bindTriggers() {
    document.querySelectorAll('[data-rsg-notify]').forEach(function (el) {
      if (el.dataset.rsgBound === '1') return;
      el.dataset.rsgBound = '1';
      el.addEventListener('click', function (e) {
        e.preventDefault();
        openModal(el);
      });
    });
  }

  function boot() { init(); bindTriggers(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);

  /*
   * Public handle. rsg-notify-me-code-spec.md §6 wires the success animation to
   * a `klaviyoForms` event, which is the embedded-form route. We do not use it
   * (see the modal snippet for why), but exposing runSuccess keeps that route
   * available if an embedded Klaviyo form is ever dropped in alongside.
   */
  window.rsgNotify = {
    runSuccess: runSuccess,
    open: openModal,
    close: closeModal
  };
})();
